/* ═══════════════════════════════════════════════════════════════
   Project mutations — persistence helpers shared by the Hub table,
   the ProjectFormPanel, and (once T4 lands) the project dashboard.
   No React here so both the hub and the dashboard can call the same
   code path and never drift on save side effects.
   ═══════════════════════════════════════════════════════════════ */
import { ProjectService } from '../../services/projectService';
import { TimeService } from '../../services/timeService';
import { AdminService } from '../../services/adminService';
import { isArchived } from '../../lib/archive';
import type {
  Project,
  ProjectAssignment,
  ProjectScopeAssignment,
  Deliverable,
} from '../../../shared/types';
import type { FirmPhaseDef } from './widgets/context';

export type { FirmPhaseDef };

/**
 * Persist a project — seeds default phases (from the firm-wide phase list)
 * for brand-new projects the server didn't already seed. PIC/PM live only
 * on the project record (`picId`/`pmId`); they are no longer mirrored
 * into project_assignments rows.
 *
 * DECISION (2026-09-25): `anticipatedHours` / `anticipatedExpenses` are
 * stripped from the payload before save. The `projects` table has no
 * matching Postgres columns — no migration ever added them — so a save
 * with either key set would 500. The form field is omitted entirely rather
 * than shipping a disabled/dead control (docs/PROJECT_CENTER_PARITY.md
 * FORM11 — flagged as a regression pending a real migration).
 */
export async function saveProject(
  project: Project,
  firmPhases: FirmPhaseDef[]
): Promise<{ project: Project }> {
  const isNew = !project.id;
  const payload: Project = { ...project };
  delete (payload as Partial<Project>).anticipatedHours;
  delete (payload as Partial<Project>).anticipatedExpenses;

  const saved = await ProjectService.save(payload);

  // The server seeds a new project's phases from the firm catalog and sets
  // currentPhaseId in the same request — only seed here when it didn't.
  if (isNew && saved.id && !saved.currentPhaseId) {
    for (let i = 0; i < firmPhases.length; i++) {
      try {
        await TimeService.savePhase({
          id: '',
          projectId: saved.id,
          name: firmPhases[i].name,
          description: '',
          status: 'pending',
          sortOrder: i,
          createdAt: '',
          updatedAt: '',
        });
      } catch (err) {
        console.error('Phase creation error on new project:', err);
      }
    }
  }

  return { project: saved };
}

/**
 * Toggle a project's assignment to a scope on/off. Re-reads live
 * assignments first (not cached state) so a rapid double-click can't create
 * duplicate rows. Moved verbatim from the old Projects page's `toggleScope` (:925-947).
 */
export async function toggleScope(
  projectId: string,
  scopeId: string
): Promise<ProjectScopeAssignment[]> {
  const current = await AdminService.projectScopeAssignments.list(projectId);
  const existingForScope = current.filter((a) => a.scopeId === scopeId);
  if (existingForScope.length > 0) {
    for (const a of existingForScope) {
      await AdminService.projectScopeAssignments.delete(a.id);
    }
  } else {
    await AdminService.projectScopeAssignments.save({
      id: '',
      projectId,
      scopeId,
      createdAt: '',
    });
  }
  return AdminService.projectScopeAssignments.list(projectId);
}

/**
 * ONE phase write path (T3b, 2026-09-28 — Josh: "phase in two places, must
 * be one"). Sets `project.currentPhaseId` directly by id — no status
 * cycling, no auto-close. Phase completion is its own real state
 * (`project_phases.status = 'complete'`, gated `admin_center.manage_billing`,
 * set via `ctx.savePhase`), not derived from which phase is "current".
 * The server enforces the id belongs to this project's own phase list and
 * refuses picking a deleted/other-project phase. Used by the Phase
 * stepper, the Project widget, and the project form — replaces the old
 * old name-based, status-cycling phase mutation entirely.
 */
export async function setCurrentPhase(
  project: Project,
  phaseId: string
): Promise<{ project: Project }> {
  const saved = await ProjectService.save({ ...project, currentPhaseId: phaseId });
  return { project: saved };
}

export type AttentionKind = 'overdue' | 'due-soon' | 'no-pm' | 'no-team';

export interface AttentionItem {
  id: string;
  kind: AttentionKind;
  projectId: string;
  title: string;
  detail: string;
  sortKey: string;
}

/**
 * Ranked "needs attention" list for the hub's AttentionStrip. Pure — no I/O
 * — so it's unit-tested directly (attention.test.ts).
 *
 * Hours-pace attention (a project burning hours faster than its phase
 * schedule implies) is deliberately OMITTED: `projects` has no persisted
 * anticipated-hours column (see `saveProject` above), so there is no stored
 * baseline to pace against yet.
 */
export function computeAttention(
  projects: Project[],
  deliverables: Deliverable[],
  projectAssignments: ProjectAssignment[],
  today: Date
): AttentionItem[] {
  const pad = (n: number) => String(n).padStart(2, '0');
  const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const todayIso = iso(today);
  const in7Iso = iso(new Date(today.getTime() + 7 * 86400000));

  const liveProjects = projects.filter((p) => !isArchived(p) && p.status !== 'closed');
  const liveIds = new Set(liveProjects.map((p) => p.id));
  const projectById = new Map(liveProjects.map((p) => [p.id, p]));

  const items: AttentionItem[] = [];

  for (const d of deliverables) {
    if (!d.dueDate || d.issued || d.status === 'complete') continue;
    if (!liveIds.has(d.projectId)) continue;
    const proj = projectById.get(d.projectId)!;
    const label = proj.projectNumber || proj.name;
    if (d.dueDate < todayIso) {
      items.push({
        id: `overdue:${d.id}`,
        kind: 'overdue',
        projectId: d.projectId,
        title: label,
        detail: `${d.name} was due ${d.dueDate}`,
        sortKey: `0:${d.dueDate}:${d.id}`,
      });
    } else if (d.dueDate <= in7Iso) {
      items.push({
        id: `due-soon:${d.id}`,
        kind: 'due-soon',
        projectId: d.projectId,
        title: label,
        detail: `${d.name} due ${d.dueDate}`,
        sortKey: `1:${d.dueDate}:${d.id}`,
      });
    }
  }

  const assignedProjectIds = new Set(projectAssignments.map((a) => a.projectId));
  for (const p of liveProjects) {
    if (p.status !== 'active') continue;
    const label = p.projectNumber || p.name;
    if (!p.pmId) {
      items.push({
        id: `no-pm:${p.id}`,
        kind: 'no-pm',
        projectId: p.id,
        title: label,
        detail: 'No PM assigned',
        sortKey: `2:${label}`,
      });
    }
    if (!p.picId && !p.pmId && !assignedProjectIds.has(p.id)) {
      items.push({
        id: `no-team:${p.id}`,
        kind: 'no-team',
        projectId: p.id,
        title: label,
        detail: 'No team assigned',
        sortKey: `3:${label}`,
      });
    }
  }

  return items.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
}
